/**
 * Hooks de la correction manuelle (docs/12 §4).
 *
 * Toute la logique de données vit ici, jamais dans le JSX (docs/24 §3).
 *
 * ⚠️ **Aucun de ces hooks ne protège quoi que ce soit.** La file ne montre que
 * les cours de l'appelant parce que le **serveur** l'a bornée, pas parce que
 * l'écran filtre ; et chaque bouton « corriger » a sa garde d'API en face, qui
 * revérifie la propriété du cours au moment du clic (docs/07 §8). Un cours cédé
 * entre l'affichage et le geste doit refuser, et c'est le serveur qui le sait.
 *
 * Ce fichier ne rejoue **aucune** règle de barème : les points d'une question
 * sont bornés côté serveur, une question ouverte non notée y fait échouer
 * l'envoi entier, et l'écran se contente de rendre le refus lisible. Recopier
 * ces règles ici en produirait une seconde version, qui divergerait.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, unwrap } from "@/shared/api/client";
import type { ApiError } from "@/shared/api/errors";
import type { components } from "@/shared/api/generated";
import { learningKeys, teachingKeys } from "@/shared/api/query-keys";

export type GradingQueueItem = components["schemas"]["GradingQueueItemResponse"];
export type Submission = components["schemas"]["SubmissionResponse"];
export type SubmittedAnswer = components["schemas"]["SubmittedAnswerResponse"];
export type GradingRecord = components["schemas"]["GradingRecordResponse"];
export type MarkInput = components["schemas"]["MarkRequest"];

/**
 * Invalide après une notation — **des deux côtés**.
 *
 * `teachingKeys` parce que la file et le tableau de bord changent ; mais aussi
 * `learningKeys`, et c'est le point qu'on oublierait : noter une copie débloque
 * une leçon chez l'élève, et un enseignant qui suit lui-même un cours verrait
 * son propre player figé. Le coût est une requête ; l'alternative est de
 * décider côté client ce qu'une note a déplacé, c'est-à-dire de rejouer RM-01.
 */
function useAfterGrading() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: teachingKeys.all });
    void queryClient.invalidateQueries({ queryKey: learningKeys.all });
  };
}

/** Les copies qui attendent la correction de l'appelant, les plus vieilles d'abord. */
export function useGradingQueue(courseId?: string): UseQueryResult<GradingQueueItem[], ApiError> {
  return useQuery({
    queryKey: teachingKeys.gradingQueue(courseId),
    queryFn: async () => {
      const page = await unwrap(
        api.GET("/api/v1/teaching/grading-queue", {
          params: { query: courseId === undefined ? {} : { course_id: courseId } },
        }),
      );
      return page.data;
    },
  });
}

/**
 * Une copie à corriger.
 *
 * `retry: false` : la réponse à une copie qui n'est pas la sienne est un 403,
 * et le réessayer trois fois retarde le message sans changer le verdict.
 */
export function useSubmission(attemptId: string | undefined): UseQueryResult<Submission, ApiError> {
  return useQuery({
    queryKey: teachingKeys.submission(attemptId ?? ""),
    enabled: attemptId !== undefined,
    retry: false,
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/teaching/attempts/{attempt_id}", {
          params: { path: { attempt_id: attemptId ?? "" } },
        }),
      ),
  });
}

export interface GradeInput {
  attemptId: string;
  marks: MarkInput[];
  feedback?: Record<string, unknown> | null;
}

export function useGradeAttempt(): UseMutationResult<Submission, ApiError, GradeInput> {
  const afterGrading = useAfterGrading();
  return useMutation({
    mutationFn: ({ attemptId, marks, feedback }: GradeInput) =>
      unwrap(
        api.POST("/api/v1/attempts/{attempt_id}/grade", {
          params: { path: { attempt_id: attemptId } },
          body: { marks, feedback: feedback ?? null },
        }),
      ),
    onSuccess: afterGrading,
  });
}

/**
 * Annule une notation — la copie retourne dans la file (INV-L5).
 *
 * Le geste **retire** à un élève une note qu'il a lue, et peut refermer la leçon
 * qu'elle avait ouverte : l'écran qui l'appelle demande confirmation, et ce
 * n'est pas une politesse.
 */
export function useRevokeGrade(): UseMutationResult<Submission, ApiError, string> {
  const afterGrading = useAfterGrading();
  return useMutation({
    mutationFn: (attemptId: string) =>
      unwrap(
        api.POST("/api/v1/attempts/{attempt_id}/regrade", {
          params: { path: { attempt_id: attemptId } },
        }),
      ),
    onSuccess: afterGrading,
  });
}

/**
 * Les questions qu'un correcteur **doit** noter avant de pouvoir envoyer.
 *
 * Lit `is_auto_graded`, servi par le serveur, plutôt que de déduire la nature
 * d'une question de son `type` : la liste des types auto-corrigeables est une
 * règle métier, elle vit dans le domaine, et la recopier ici la ferait diverger
 * au premier type ajouté — `file_upload` arrive en phase 8.
 *
 * Le bouton reste **actif** même quand il en manque : c'est le serveur qui
 * refuse, avec la liste des questions manquantes, et un bouton grisé sans
 * explication laisse l'utilisateur chercher pourquoi.
 */
export function questionsNeedingAMark(submission: Submission): SubmittedAnswer[] {
  return submission.answers.filter((answer) => !answer.is_auto_graded);
}

/** La notation en vigueur, s'il y en a une — les annulées restent dans l'historique. */
export function currentGrading(submission: Submission): GradingRecord | undefined {
  return submission.gradings.filter((item) => item.revoked_at === null).at(-1);
}
